<template>
  <div class="flexing flex-col w-full">
    <form class="w-full p-6 space-y-6" @submit.prevent>
      <h1 class="text-texts-standard-important font-medium text-36">
        {{ $t('settings.changeEmail') }}
      </h1>
      <p class="text-texts-standard-default text-28 font-medium !mt-2">
        {{ $t('settings.enterEmailToVerify') }}
      </p>
      <div>
        <div class="v-field--variant-outlined relative">
          <input
            v-model.trim="emailInput"
            v-focus
            :class="{ 'is-error': errors.email.size > 0 }"
            type="text"
            name="email"
            :placeholder="$t('settings.newEmail')"
            :disabled="isLoading"
          />
          <div class="v-field__outline" v-html="inputLabel('settings.newEmail')" />
        </div>
        <div class="validation-text__wrapper">
          <div class="validation-text" :class="{ 'validation-text--error': errors.email.size > 0 }">
            <span v-for="(error, i) in errors.email" :key="i">
              {{ error }}
            </span>
          </div>
        </div>
      </div>
      <div>
        <div class="v-field--variant-outlined relative">
          <input
            v-model.trim="pwdInput"
            :class="{ 'is-error': errors.password.size > 0 }"
            :type="isPasswordVisible ? 'text' : 'password'"
            name="password"
            :placeholder="$t('settings.currentPassword')"
            :disabled="isLoading"
          />
          <div class="v-field__outline" v-html="inputLabel('settings.currentPassword')" />
          <div
            class="input-icon cursor-pointer absolute top-1/2 transform -translate-y-1/2 right-16"
            :class="isPasswordVisible ? 'is-password-visible-icon' : 'is-password-hidden-icon'"
            @click="isPasswordVisible = !isPasswordVisible"
          />
          <tippy
            class="absolute top-1/2 transform -translate-y-1/2 right-4"
            theme="WSM"
            placement="top"
          >
            <div class="input-icon is-info-icon cursor-pointer" />
            <template #content>
              <p>{{ passwordTooltip }}</p>
            </template>
          </tippy>
        </div>
        <div class="validation-text__wrapper">
          <div
            class="validation-text"
            :class="{ 'validation-text--error': errors.password.size > 0 }"
          >
            <span v-for="(error, i) in errors.password" :key="i">
              {{ error }}
            </span>
          </div>
        </div>
      </div>
      <p class="text-texts-standard-default text-28 !mt-2">
        <span class="uppercase underline cursor-pointer" @click="$emit('back')">
          {{ $t('homepage.forgotPassword') }}
        </span>
      </p>
      <app-button type="submit" btn-type="confirm" :disabled="isLoading" @click="submitEmailChange">
        {{ $t('settings.change') }}
        <component-loading class="absolute inset-0" :is-loading="isLoading" />
      </app-button>
    </form>
  </div>
</template>

<script lang="ts">
import ComponentLoading from '../GlobalComponents/ComponentLoading.vue'
import { defineComponent, type PropType } from 'vue'
import type { FormErrors } from '@/interfaces/login/Login'
import { PASSWORD_MIN_LENGTH, PASSWORD_MAX_LENGTH } from '@/data/login/constants'

interface ComponentData {
  PASSWORD_MIN_LENGTH: typeof PASSWORD_MIN_LENGTH
  PASSWORD_MAX_LENGTH: typeof PASSWORD_MAX_LENGTH
  isPasswordVisible: boolean
  emailInput: string
  pwdInput: string
}

export default defineComponent({
  components: {
    ComponentLoading,
  },
  props: {
    password: {
      type: String,
      required: true,
    },
    email: {
      type: String,
      required: true,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    errors: {
      type: Object as PropType<FormErrors>,
      default: () => {},
    },
  },
  emits: ['update:email', 'update:password', 'back', 'submit'],
  data(): ComponentData {
    return {
      PASSWORD_MIN_LENGTH,
      PASSWORD_MAX_LENGTH,
      isPasswordVisible: false,
      emailInput: this.email,
      pwdInput: this.password,
    }
  },
  computed: {
    passwordTooltip(): string {
      return this.$replacePlaceholder(
        this.$replacePlaceholder(
          this.$t('homepage.errorPasswordInvalidFormat'),
          /%s/,
          this.PASSWORD_MIN_LENGTH.toString(),
        ),
        /%s/,
        this.PASSWORD_MAX_LENGTH.toString(),
      )
    },
  },
  watch: {
    emailInput(): void {
      this.$emit('update:email', this.emailInput)
    },
    pwdInput(): void {
      this.$emit('update:password', this.pwdInput)
    },
  },
  methods: {
    inputLabel(translatedLabel: string): string {
      return `
        <div class="v-field__outline__start"></div>
        <div class="v-field__outline__notch">
          <label class="v-label v-field-label" aria-hidden="true">
            ${this.$t(translatedLabel)}
          </label>
        </div>
        <div class="v-field__outline__end"></div>
      `
    },
    submitEmailChange(): void {
      this.$emit('submit')
    },
  },
})
</script>
